import { api } from './api';
import type { Product } from './products.service';

// Backwards compatibility alias
type Course = Product;

export interface MemberArea {
  id: string;
  name: string;
  slug: string;
  description?: string;
  logo?: string;
  favicon?: string;
  themeColors?: {
    primary?: string;
    secondary?: string;
    background?: string;
    text?: string;
  };
  customCss?: string;
  menuItems?: MenuItem[];
  isActive: boolean;
  accessGroups?: AccessGroupInfo[];
  createdAt: string;
  updatedAt: string;
}

export interface MenuItem {
  title: string;
  url: string;
  icon?: string;
  openInNewTab?: boolean;
  order?: number;
}

export interface AccessGroupInfo {
  id: string;
  name: string;
  isActive: boolean;
  coursesCount: number;
  enrollmentsCount?: number;
  courses?: Course[];
}

class MemberAreasService {
  // User methods
  async getMyMemberAreas(): Promise<MemberArea[]> {
    const { data } = await api.get('/member-areas/my');
    return data;
  }

  async getMemberAreaBySlug(slug: string): Promise<MemberArea> {
    const { data } = await api.get(`/member-areas/slug/${slug}`);
    return data;
  }

  async getMemberAreaCourses(memberAreaId: string): Promise<Course[]> {
    const { data } = await api.get(`/member-areas/${memberAreaId}/courses`);
    return data;
  }

  async checkAccess(memberAreaId: string): Promise<{ hasAccess: boolean }> {
    const { data } = await api.get(
      `/member-areas/${memberAreaId}/check-access`,
    );
    return data;
  }

  // Admin methods
  async findAll(
    params: {
      page?: number;
      limit?: number;
      search?: string;
      isActive?: boolean;
    } = {},
  ): Promise<{
    data: MemberArea[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });

    const { data } = await api.get(
      `/member-areas/admin?${searchParams.toString()}`,
    );
    return data;
  }

  async findById(id: string): Promise<MemberArea> {
    const { data } = await api.get(`/member-areas/admin/${id}`);
    return data;
  }

  async create(
    memberArea: Partial<MemberArea> & {
      accessGroupIds?: string[];
    },
  ): Promise<MemberArea> {
    const { data } = await api.post('/member-areas', memberArea);
    return data;
  }

  async update(
    id: string,
    memberArea: Partial<MemberArea> & { accessGroupIds?: string[] },
  ): Promise<MemberArea> {
    const { data } = await api.put(`/member-areas/${id}`, memberArea);
    return data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/member-areas/${id}`);
  }

  // ==================== BANNERS ====================

  async getBanners(): Promise<Banner[]> {
    const { data } = await api.get('/member-areas/banners/admin');
    return data?.data || data;
  }

  async getPublicBanners(position?: string): Promise<Banner[]> {
    const params = position ? `?position=${position}` : '';
    const { data } = await api.get(`/member-areas/banners/public${params}`);
    return data?.data || data;
  }

  async createBanner(
    banner: Omit<Banner, 'id' | 'createdAt' | 'updatedAt'>,
  ): Promise<Banner> {
    const { data } = await api.post('/member-areas/banners', banner);
    return data?.data || data;
  }

  async updateBanner(id: string, banner: Partial<Banner>): Promise<Banner> {
    const { data } = await api.put(`/member-areas/banners/${id}`, banner);
    return data?.data || data;
  }

  async deleteBanner(id: string): Promise<void> {
    await api.delete(`/member-areas/banners/${id}`);
  }

  async reorderBanners(ids: string[]): Promise<void> {
    await api.put('/member-areas/banners/reorder', { ids });
  }

  // ==================== MENU ITEMS ====================

  async getMenuItems(): Promise<CustomMenuItem[]> {
    const { data } = await api.get('/member-areas/menus/admin');
    return data?.data || data;
  }

  async getPublicMenuItems(): Promise<CustomMenuItem[]> {
    const { data } = await api.get('/member-areas/menus/public');
    return data?.data || data;
  }

  async createMenuItem(
    item: Omit<CustomMenuItem, 'id' | 'createdAt' | 'updatedAt'>,
  ): Promise<CustomMenuItem> {
    const { data } = await api.post('/member-areas/menus', item);
    return data?.data || data;
  }

  async updateMenuItem(
    id: string,
    item: Partial<CustomMenuItem>,
  ): Promise<CustomMenuItem> {
    const { data } = await api.put(`/member-areas/menus/${id}`, item);
    return data?.data || data;
  }

  async deleteMenuItem(id: string): Promise<void> {
    await api.delete(`/member-areas/menus/${id}`);
  }

  async reorderMenuItems(ids: string[]): Promise<void> {
    await api.put('/member-areas/menus/reorder', { ids });
  }

  // ==================== PLATFORM SETTINGS ====================

  async getPlatformSettings(): Promise<PlatformSettings> {
    const { data } = await api.get('/member-areas/settings/public');
    return data?.data || data;
  }

  async updatePlatformSettings(
    settings: Partial<PlatformSettings>,
  ): Promise<PlatformSettings> {
    const { data } = await api.put('/member-areas/settings', settings);
    return data?.data || data;
  }
}

export interface PlatformSettings {
  whatsappNumber: string;
  whatsappMessage: string;
  whatsappEnabled: boolean;
  emailWebhookUrl: string;
}

export interface Banner {
  id: string;
  title?: string;
  imageUrl: string;
  linkUrl?: string;
  position: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CustomMenuItem {
  id: string;
  title: string;
  icon?: string;
  url?: string;
  isExternal: boolean;
  openNewTab: boolean;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const memberAreasService = new MemberAreasService();
